/* eslint-disable indent */
export class CampaignDataAdapter {
    // Function to extract plain text from Slate.js structure
    static extractTextFromSlate(slateData) {
        if (Array.isArray(slateData)) {
            return slateData
                .map((node) => {
                    if (node.children && Array.isArray(node.children)) {
                        return node.children
                            .map((child) => child.text || '')
                            .join('');
                    }
                    return node.text || '';
                })
                .join('\n');
        }
        return '';
    }

    // Function to create a simple Slate.js structure from plain text
    static createSlateFromText(text) {
        if (!text || text.trim() === '') {
            return [{ type: 'paragraph', children: [{ text: '' }] }];
        }

        // Split text by newlines and create paragraphs
        const lines = text.split('\n');
        return lines.map((line) => ({
            type: 'paragraph',
            children: [{ text: line }]
        }));
    }

    static transformForUi(data) {
        if (!data || Object.keys(data).length === 0) {
            return {
                imageUrl: null,
                campaignName: '',
                languages: [],
                notificationDetails: {},
                status: 'draft',
                // users: [],
                displayEndDate: 'no',
                selectedAudiences: 'selectedAudience', // By default - selected audiences
                audiences: [],
                location: null
            };
        }

        // Safely parse JSON field
        let languages = [];
        let titles = {};
        let texts = {};
        let users = [];

        try {
            // Parse languages
            if (data.languages) {
                if (typeof data.languages === 'string') {
                    languages = JSON.parse(data.languages);
                } else if (Array.isArray(data.languages)) {
                    languages = data.languages;
                }
            }

            // Parse title
            if (data.title) {
                if (typeof data.title === 'string') {
                    try {
                        titles = JSON.parse(data.title);
                    } catch (e) {
                        console.error('Error parsing title field:', e);
                        titles = {};
                    }
                } else if (typeof data.title === 'object') {
                    titles = data.title;
                }
            }

            // Parse text
            if (data.text) {
                if (typeof data.text === 'string') {
                    try {
                        // First parse main JSON
                        const parsedText = JSON.parse(data.text);

                        // Now process each language - support both Slate.js and plain text
                        texts = {};
                        Object.keys(parsedText).forEach((lang) => {
                            if (typeof parsedText[lang] === 'string') {
                                // Check if this is a JSON structure for Slate.js (starts with [{ and contains type)
                                if (
                                    parsedText[lang].startsWith('[{') &&
                                    parsedText[lang].includes('type')
                                ) {
                                    try {
                                        // Parse JSON for Slate.js structure and extract text
                                        const slateData = JSON.parse(
                                            parsedText[lang]
                                        );

                                        // Extract plain text from Slate.js structure
                                        texts[lang] =
                                            CampaignDataAdapter.extractTextFromSlate(
                                                slateData
                                            );
                                    } catch (e) {
                                        console.error(
                                            `Error parsing Slate.js text for language ${lang}:`,
                                            e
                                        );
                                        // If parsing fails - leave as string
                                        texts[lang] = parsedText[lang];
                                    }
                                } else {
                                    // This is already plain text - use as is
                                    texts[lang] = parsedText[lang];
                                }
                            } else {
                                // If already object - try to extract text (for old data)
                                texts[lang] =
                                    CampaignDataAdapter.extractTextFromSlate(
                                        parsedText[lang]
                                    );
                            }
                        });
                    } catch (e) {
                        console.error('Error parsing text field:', e);
                        texts = {};
                    }
                } else if (typeof data.text === 'object') {
                    texts = data.text;
                }
            }

            // Parse users
            if (data.users) {
                if (typeof data.users === 'string') {
                    users = JSON.parse(data.users);
                } else if (Array.isArray(data.users)) {
                    users = data.users;
                }
            }
        } catch (error) {
            console.error('Error parsing JSON fields in campaign data:', error);
        }

        // Create notificationDetails from titles and texts
        const notificationDetails = languages.reduce((details, lang) => {
            details[lang] = {
                title: titles[lang] || '',
                text: texts[lang] || ''
            };
            return details;
        }, {});

        // Process audiences - priority to audienceLists, then audienceListIds
        let audiences = [];

        if (data.audienceLists && Array.isArray(data.audienceLists)) {
            // If full audienceLists data exists - use it
            audiences = data.audienceLists.map((audience) => ({
                id: audience.id,
                name: audience.name,
                usersCount: audience.usersCount,
                type: audience.type,
                description: audience.description,
                // Add all other fields for compatibility
                ...audience
            }));
        } else if (
            data.audienceListIds &&
            Array.isArray(data.audienceListIds)
        ) {
            // If only audience IDs, create objects with basic info
            audiences = data.audienceListIds.map((id, index) => ({
                id: id,
                name: data.audienceListsNames
                    ? data.audienceListsNames.split(', ')[index] ||
                      `Audience ${id}`
                    : `Audience ${id}`,
                usersCount:
                    Math.floor(
                        data.audienceListUserNumber /
                            data.audienceListIds.length
                    ) || 0
            }));
        }

        // Parse deep link - expecting single string
        let deepLink = null;
        if (data.deepLink) {
            if (typeof data.deepLink === 'string') {
                deepLink = data.deepLink;
            }
        }

        const result = {
            ...data,
            languages,
            users,
            notificationDetails,
            audiences,
            // Always set mode "Selected Audience Lists"
            selectedAudiences: 'selectedAudience',
            // Transform imageUrl for UI if present
            imageUrl: data.imageUrl
                ? {
                      imageUrl: data.imageUrl,
                      name: data.imageUrl.split('/').pop() || 'campaign-image',
                      url: data.imageUrl
                  }
                : null,
            // Transform displayEndDate to string for UI
            displayEndDate: data.displayEndDate ? 'yes' : 'no',
            // Set location from locationId
            location: data.locationId || null,
            // Add scheduledDate and notificationTiming
            scheduledDate: data.scheduledDate || null,
            notificationTiming: data.notificationTiming || 'sendNow',
            // Add campaign end date - map from backend offerEndDate
            campaignEndDate: data.offerEndDate || data.campaignEndDate || null,
            // Add deep link
            deepLink: deepLink
        };

        return result;
    }

    static prepareForRequest(data) {
        const { notificationDetails } = data;

        // Prepare data in the format expected by the server
        const requestData = {
            // Main campaign fields
            campaignName: data.campaignName || '',
            // Do not change status here, it will be set in handleSend

            // URL image (if an image is uploaded)
            imageUrl: data.imageUrl?.imageUrl || data.imageUrl || null,

            // Audience data
            audienceListIds: data.audiences
                ? data.audiences.map((audience) => audience.id)
                : [],
            audienceListsNames: data.audiences
                ? data.audiences.map((audience) => audience.name).join(', ')
                : '',
            audienceListUserNumber: data.audiences
                ? data.audiences.reduce(
                      (sum, audience) => sum + (audience.usersCount || 0),
                      0
                  )
                : 0,

            // Metrics (null for new campaigns)
            deliveryRate: data.deliveryRate || null,
            clickRate: data.clickRate || null,

            // Additional data for internal processing
            languages: JSON.stringify(data.languages || []),

            title:
                notificationDetails && Object.keys(notificationDetails).length
                    ? JSON.stringify(
                          Object.fromEntries(
                              Object.entries(notificationDetails).map(
                                  ([lang, content]) => [lang, content.title]
                              )
                          )
                      )
                    : '{}',

            text:
                notificationDetails && Object.keys(notificationDetails).length
                    ? JSON.stringify(
                          Object.fromEntries(
                              Object.entries(notificationDetails).map(
                                  ([lang, content]) => [
                                      lang,
                                      content.text || ''
                                  ]
                              )
                          )
                      )
                    : '{}',

            locationId: data.location || null,
            displayEndDate: data.displayEndDate === 'yes',

            // Sending time data
            scheduledDate: data.scheduledDate || null,
            notificationTiming: data.notificationTiming || null,

            // Campaign end date data - send as offerEndDate to backend
            offerEndDate: data.campaignEndDate || null,

            // Deep link data - send as single string
            deepLink: data.deepLink || null
        };

        return requestData;
    }
}
